import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { liveReportingQueryKeys } from 'src/queries/liveReporting/liveReporting.keys'
import type { LiveReportSummary } from 'src/queries/liveReporting/liveReporting.models'
import { liveReportingQueries } from 'src/queries/liveReporting/liveReporting.queries'
import { scrubReportingPrivateCache } from 'src/queries/reporting/reporting.cache'
import { reportingErrorStatus } from 'src/queries/reporting/reporting.errors'

import { useVisibleReportIds } from './useVisibleReportIds'

export const MAX_VISIBLE_LIVE_RESULT_REQUESTS = 4

export type LiveReportLoadState =
  | 'idle'
  | 'loading'
  | 'success'
  | 'not_found'
  | 'unavailable'
  | 'incompatible'

export interface VisibleLiveReportState {
  error?: unknown
  status: LiveReportLoadState
}

export interface UseVisibleLiveReportsOptions {
  actorId?: string
  organizationId?: string
  reports: readonly LiveReportSummary[]
  reportingEnabled?: boolean
  onAccessDenied?: (status: number) => void
}

export interface UseVisibleLiveReportsResult {
  register: (reportId: string) => (node: HTMLElement | null) => void
  reports: ReadonlyMap<string, VisibleLiveReportState>
  refresh: (reportId?: string) => void
}

const loadStateFor = (error: unknown): LiveReportLoadState => {
  const status = reportingErrorStatus(error)
  if (status === 404) return 'not_found'
  if (status === 422) return 'incompatible'
  return 'unavailable'
}

/**
 * Loads only cards near the viewport and serializes browser fetches through a
 * four-slot queue. Results are deliberately kept in React Query's in-memory
 * cache only; this hook never persists a dataset or snapshot.
 */
export const useVisibleLiveReports = ({
  actorId,
  organizationId,
  reports,
  reportingEnabled = true,
  onAccessDenied,
}: UseVisibleLiveReportsOptions): UseVisibleLiveReportsResult => {
  const queryClient = useQueryClient()
  const [states, setStates] = useState<
    ReadonlyMap<string, VisibleLiveReportState>
  >(() => new Map())
  const queueRef = useRef<string[]>([])
  const queuedRef = useRef(new Set<string>())
  const inFlightRef = useRef(new Set<string>())
  const requestVersionRef = useRef(new Map<string, number>())
  const pumpRef = useRef<(() => void) | null>(null)
  const generationRef = useRef(0)
  const scopeRef = useRef({ actorId, organizationId })

  const reportIds = useMemo(() => reports.map((report) => report.id), [reports])
  const reportIdsRef = useRef(reportIds)
  reportIdsRef.current = reportIds
  const { register, visibleIds } = useVisibleReportIds({
    reportIds,
    actorId,
    organizationId,
    enabled: reportingEnabled,
  })

  const pump = useCallback((): void => {
    if (!reportingEnabled || !actorId || !organizationId) return
    while (
      inFlightRef.current.size < MAX_VISIBLE_LIVE_RESULT_REQUESTS &&
      queueRef.current.length > 0
    ) {
      const reportId = queueRef.current.shift()!
      queuedRef.current.delete(reportId)
      if (inFlightRef.current.has(reportId)) continue
      inFlightRef.current.add(reportId)
      const generation = generationRef.current
      const requestVersion = (requestVersionRef.current.get(reportId) ?? 0) + 1
      requestVersionRef.current.set(reportId, requestVersion)
      setStates((current) => {
        const next = new Map(current)
        next.set(reportId, { status: 'loading' })
        return next
      })
      const options = liveReportingQueries.result({
        actorId,
        organizationId,
        reportId,
        reportingEnabled,
      })
      void queryClient
        .fetchQuery(options)
        .then(() => {
          if (
            generation !== generationRef.current ||
            requestVersion !== requestVersionRef.current.get(reportId)
          )
            return
          setStates((current) => {
            const next = new Map(current)
            next.set(reportId, { status: 'success' })
            return next
          })
        })
        .catch((error: unknown) => {
          if (
            generation !== generationRef.current ||
            requestVersion !== requestVersionRef.current.get(reportId)
          )
            return
          const status = reportingErrorStatus(error)
          if (status === 401 || status === 403) {
            generationRef.current += 1
            queueRef.current = []
            queuedRef.current.clear()
            inFlightRef.current.clear()
            requestVersionRef.current.clear()
            scrubReportingPrivateCache(queryClient, actorId, organizationId)
            setStates(
              new Map(
                reportIdsRef.current.map((id) => [
                  id,
                  { status: 'unavailable', error } as const,
                ]),
              ),
            )
            onAccessDenied?.(status)
            return
          }
          setStates((current) => {
            const next = new Map(current)
            next.set(reportId, { status: loadStateFor(error), error })
            return next
          })
        })
        .finally(() => {
          if (
            generation === generationRef.current &&
            requestVersion === requestVersionRef.current.get(reportId)
          ) {
            inFlightRef.current.delete(reportId)
            pumpRef.current?.()
          }
        })
    }
  }, [actorId, onAccessDenied, organizationId, queryClient, reportingEnabled])

  pumpRef.current = pump

  useEffect(() => {
    const previous = scopeRef.current
    if (
      previous.actorId !== actorId ||
      previous.organizationId !== organizationId ||
      !reportingEnabled
    ) {
      generationRef.current += 1
      scrubReportingPrivateCache(
        queryClient,
        previous.actorId,
        previous.organizationId,
      )
      setStates(new Map())
      queueRef.current = []
      queuedRef.current.clear()
      inFlightRef.current.clear()
      requestVersionRef.current.clear()
    }
    scopeRef.current = { actorId, organizationId }
  }, [actorId, organizationId, queryClient, reportingEnabled])

  useEffect(() => {
    const currentReportIds = new Set(reports.map((report) => report.id))
    setStates((current) => {
      const next = new Map(
        [...current].filter(([id]) => currentReportIds.has(id)),
      )
      return next.size === current.size ? current : next
    })
    for (const reportId of visibleIds) {
      if (
        !currentReportIds.has(reportId) ||
        queuedRef.current.has(reportId) ||
        inFlightRef.current.has(reportId)
      )
        continue
      const current = states.get(reportId)
      if (current) continue
      queueRef.current.push(reportId)
      queuedRef.current.add(reportId)
    }
    pump()
  }, [pump, reports, states, visibleIds])

  useEffect(
    () => () => {
      generationRef.current += 1
      for (const reportId of inFlightRef.current) {
        void queryClient.cancelQueries?.({
          queryKey: liveReportingQueryKeys.result(
            scopeRef.current.actorId,
            scopeRef.current.organizationId,
            reportId,
            'live',
          ),
        })
      }
      queueRef.current = []
      queuedRef.current.clear()
      inFlightRef.current.clear()
      requestVersionRef.current.clear()
    },
    [queryClient],
  )

  const refresh = useCallback(
    (reportId?: string): void => {
      const ids = reportId ? [reportId] : [...visibleIds]
      for (const id of ids) {
        const queryKey = liveReportingQueryKeys.result(
          actorId,
          organizationId,
          id,
          'live',
        )
        if (actorId && organizationId && reportingEnabled) {
          void queryClient.invalidateQueries?.({
            queryKey,
          })
        }
        setStates((current) => {
          const next = new Map(current)
          next.delete(id)
          return next
        })
        if (inFlightRef.current.has(id)) {
          const generation = generationRef.current
          inFlightRef.current.delete(id)
          requestVersionRef.current.set(
            id,
            (requestVersionRef.current.get(id) ?? 0) + 1,
          )
          void Promise.resolve(queryClient.cancelQueries?.({ queryKey })).then(
            () => {
              if (
                generation !== generationRef.current ||
                actorId !== scopeRef.current.actorId ||
                organizationId !== scopeRef.current.organizationId ||
                !reportingEnabled ||
                queuedRef.current.has(id) ||
                inFlightRef.current.has(id)
              )
                return
              queueRef.current.push(id)
              queuedRef.current.add(id)
              pumpRef.current?.()
            },
          )
          continue
        }
        if (!queuedRef.current.has(id)) {
          queueRef.current.push(id)
          queuedRef.current.add(id)
        }
      }
      pump()
    },
    [pump, visibleIds],
  )

  return { register, reports: states, refresh }
}
